import { FC, useCallback, useEffect } from 'react';
import TrashCan from '../svgs/TrashCan.svg';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import './embryoDetailsTable.scss';

export type StrawRow = {
  embryoStrawCode?: string;
  embryoStrawNumber: string;
  embryoGrade?: string;
  embryoStage: string;
  embryoCountPerStraw: number;
  storageCanisterId?: number;
  lockedCode?: boolean;
  specimenId?: number;
};

export type EmbryoDetailsTableProps = {
  data: StrawRow[];
  onChange: (data: StrawRow[], valid: boolean) => void;
  globalStartIndex?: number;
};

const createEmptyStraw = (): StrawRow => ({
  embryoStrawNumber: '',
  embryoGrade: '',
  embryoStage: '',
  embryoCountPerStraw: 0,
});

const EmbryoDetailsTable: FC<EmbryoDetailsTableProps> = ({ data, onChange, globalStartIndex = 0 }) => {
  const resequenceCodes = (rows: StrawRow[]): StrawRow[] => {
    let counter = globalStartIndex;

    return rows.map((row: StrawRow) => {
      if (row.lockedCode) {
        const numeric = Number(String(row.embryoStrawNumber).replace(/^0+/, ''));
        if (!Number.isNaN(numeric) && numeric > 0) {
          counter = Math.max(counter, numeric);
        } else {
          counter += 1;
        }
        return row;
      }
      counter += 1;
      return {
        ...row,
        embryoStrawNumber: String(counter),
      };
    });
  };

  const handleUpdate = useCallback(
    (index: number, field: keyof StrawRow, value: any, lockCode = false) => {
      const newData = [...data];

      newData[index] = {
        ...newData[index],
        [field]: value,
        ...(field === 'embryoStrawNumber' && lockCode ? { lockedCode: true } : {}),
      };

      onChange(newData, true);
    },
    [data, onChange],
  );

  const handleLockOnBlur = useCallback(
    (index: number) => {
      const row = data[index];
      if (!row) return;

      const value = String(row.embryoStrawNumber ?? '').trim();
      const isPureNumeric = /^[0-9]+$/.test(value);
      if (value.length > 0 && !isPureNumeric) {
        handleUpdate(index, 'embryoStrawNumber', value, true);
      } else {
        handleUpdate(index, 'embryoStrawNumber', value, false);
      }
    },
    [data, handleUpdate],
  );

  useEffect(() => {
    const updatedData = data.map((strawRow: StrawRow, rowIndex: number) => {
      if (strawRow.embryoStrawNumber) return strawRow;

      const nextCode = String(rowIndex + 1).padStart(2, '0');
      return { ...strawRow, embryoStrawNumber: nextCode };
    });

    const hasChanges = updatedData.some(
      (updatedRow, rowIndex) => updatedRow.embryoStrawNumber !== data[rowIndex].embryoStrawNumber,
    );

    if (hasChanges) {
      onChange(updatedData, true);
    }
  }, []);

  const handleAddRow = useCallback(() => {
    const newData = [...data, createEmptyStraw()];
    onChange(newData, true);
  }, [data, onChange]);

  const handleDeleteRow = useCallback(
    (index: number) => {
      const newData = data.filter((_, i) => i !== index);
      onChange(newData, true);
    },
    [data, onChange],
  );

  useEffect(() => {
    const updatedStrawNum = resequenceCodes(data);
    const changedStrawNum = updatedStrawNum.some(
      (row: StrawRow, index: number) => row.embryoStrawNumber !== data[index].embryoStrawNumber,
    );
    if (changedStrawNum) onChange(updatedStrawNum, true);
  }, [data.length, globalStartIndex]);

  const totalQuantity = data.reduce((acc, straw) => acc + (straw.embryoCountPerStraw || 0), 0);

  return (
    <>
      <div className="desk-embryo-details">
        <div className="embryo-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                <th>{TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}</th>
                <th>{TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}</th>
                <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
                <th>{TABLE_HEADER_CONSTANTS.ACTION}</th>
              </tr>
            </thead>
            <tbody>
              <>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={5}>{LABEL_CONSTANTS.NO_STRAWS_AVAILABLE_FOR_OWNER}</td>
                  </tr>
                ) : (
                  <>
                    {data.map((row: StrawRow, index: number) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={row.embryoStrawNumber}
                            onChange={e => {
                              const value = e.target.value.replace(/\s+/g, '');
                              handleUpdate(index, 'embryoStrawNumber', value, false);
                            }}
                            onBlur={() => handleLockOnBlur(index)}
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            placeholder="0"
                            value={row.embryoStage ?? ''}
                            onChange={e => {
                              const value = e.target.value.replace(/\s+/g, '');
                              handleUpdate(index, 'embryoStage', value);
                            }}
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            placeholder="0"
                            value={row.embryoGrade ?? ''}
                            onChange={e => {
                              const value = e.target.value.replace(/\s+/g, '');
                              handleUpdate(index, 'embryoGrade', value);
                            }}
                          />
                        </td>

                        <td>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            placeholder="0"
                            value={row.embryoCountPerStraw ?? ''}
                            onChange={e => {
                              const value = e.target.value.replace(/[^0-9]/g, '');
                              handleUpdate(index, 'embryoCountPerStraw', value === '' ? '' : Number(value));
                            }}
                          />
                        </td>

                        <td>
                          <TrashCan onClick={() => handleDeleteRow(index)} className="trash-can" />
                        </td>
                      </tr>
                    ))}
                  </>
                )}
                <tr className="total-row">
                  <td className="add-btn-container">
                    <button type="button" className="add-btn" onClick={handleAddRow}>
                      +
                    </button>
                  </td>
                  <td></td>
                  <td>
                    <span className="total-quantity-label">{LABEL_CONSTANTS.TOTAL_QUANTITY}:</span>
                  </td>
                  <td>
                    <input type="text" disabled value={totalQuantity} />
                  </td>
                  <td></td>
                </tr>
              </>
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="xs-embryo-details">
        {data.length === 0 ? (
          <div className="card max-width no-straws">{LABEL_CONSTANTS.NO_STRAWS_AVAILABLE_FOR_OWNER}</div>
        ) : (
          data.map((row: StrawRow, index: number) => (
            <div className="card max-width" key={index}>
              <div className="card-row">
                <div className="card-fields">
                  <div className="card-content-section">
                    <div className="left column">
                      <label>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}:</label>
                      <input
                        type="text"
                        value={row.embryoStrawNumber}
                        onChange={e => handleUpdate(index, 'embryoStrawNumber', e.target.value, false)}
                        onBlur={() => handleLockOnBlur(index)}
                      />
                    </div>
                    <div className="right column">
                      <label>{TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}:</label>
                      <input
                        type="text"
                        placeholder="0"
                        value={row.embryoStage ?? ''}
                        onChange={e => {
                          const value = e.target.value.replace(/\s+/g, '');
                          handleUpdate(index, 'embryoStage', value);
                        }}
                      />
                    </div>
                  </div>

                  <div className="card-content-section">
                    <div className="left column">
                      <label>{TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}:</label>
                      <input
                        type="text"
                        placeholder="0"
                        value={row.embryoGrade ?? ''}
                        onChange={e => {
                          const value = e.target.value.replace(/\s+/g, '');
                          handleUpdate(index, 'embryoGrade', value);
                        }}
                      />
                    </div>
                    <div className="right column">
                      <label>{TABLE_HEADER_CONSTANTS.QUANTITY}:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        placeholder="0"
                        value={row.embryoCountPerStraw ?? ''}
                        onChange={e => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          handleUpdate(index, 'embryoCountPerStraw', value === '' ? '' : Number(value));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="card-trash">
                  <TrashCan onClick={() => handleDeleteRow(index)} className="trash-can" />
                </div>
              </div>
            </div>
          ))
        )}

        <div className="add-btn-container">
          <button type="button" className="add-btn" onClick={handleAddRow}>
            +
          </button>
        </div>

        <div className="total-quantity">
          <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}:&nbsp;</label>
          {totalQuantity}
        </div>
      </div>
    </>
  );
};

export default EmbryoDetailsTable;
